// @ts-strict-ignore
import { Footer, Header, Page, PageTransition } from '@components/Page'
import { pickSeoProps } from '@components/Page/Page'
import FeaturedTour from '@components/_tours/FeaturedTour'
import TourArrangementCard from '@components/_tours/TourArrangementCard'
import TripAdvisor from '@components/_tours/TripAdvisor'
import Usps from '@components/_tours/Usps'
import { Box, Container, Grid, OldHeading, OldText } from '@gassan-ui'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { ToursPage as ToursPageModel } from '@lib/kontent/models/ToursPage'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { rem } from 'polished'

type Props = SingleItemResponse<ToursPageModel>

const ToursPage: NextPage<Props> = ({ item, modular_content }) => {
  const { t } = useTranslation('other')
  const { locales } = useRouter()
  const firstFeatured = modular_content[item.elements.first_featured_tour.value[0]]
  const secondFeatured = modular_content[item.elements.second_featured_tour.value[0]]
  const tours = item.elements.other_tours.value.map((tour) => modular_content[tour])
  const arrangements = item.elements.arrangement_items.value.map(
    (arrangement) => modular_content[arrangement],
  )

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'tours')}
    >
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container maxWidth={rem(1450)}>
          <Box
            maxWidth={rem(840)}
            pt={{ _: '1.5rem', small: '3rem', large: '5rem', xlarge: '7rem' }}
            pb={{ _: '2.5rem', large: '4rem', xlarge: '5rem' }}
          >
            <OldHeading variant="h1" as="h1" mb={['1rem', '1.5rem']}>
              {item.elements.title.value}
            </OldHeading>
            <OldText mb={0}>{item.elements.description.value}</OldText>
          </Box>
        </Container>
        <FeaturedTour variant="text-video" item={firstFeatured.elements} />
        <Usps />
        <FeaturedTour variant="video-text" item={secondFeatured.elements} />
        <Container
          display="grid"
          maxWidth={rem(1450)}
          mt={{ _: '1.25rem', small: '3rem', xlarge: '5rem' }}
          gridTemplateColumns={{ _: '1fr', medium: '1fr 1fr', large: 'repeat(3, 1fr)' }}
          gridGap={{ _: '1.25rem', small: '3rem', medium: '1.25rem', large: '1.25rem' }}
        >
          {tours.map((tour, index) => (
            <TourArrangementCard item={tour.elements} variant="tour" key={index} />
          ))}
        </Container>
        <Container
          maxWidth={rem(1450)}
          mt={{ _: '3rem', small: '6.5rem', large: '5.5rem', xlarge: '7.5rem' }}
        >
          <OldHeading
            variant="h2"
            as="h2"
            textAlign={{ _: 'center', large: 'left' }}
            mb={{ _: '1.5rem', small: '3rem', large: '2.5rem', xlarge: '3.5rem' }}
          >
            {item.elements.arrangement_title.value}
          </OldHeading>
          {arrangements.length === 0 && (
            <OldText mt={{ _: '0rem', small: '-2rem' }} textAlign={{ _: 'center', large: 'left' }}>
              {t('noArrangementsAvailable')}
            </OldText>
          )}
          <Grid
            gridTemplateColumns={{ _: '1fr', medium: '1fr 1fr', large: 'repeat(3, 1fr)' }}
            gridGap={{ _: '1.25rem', small: '3rem', medium: '1.25rem', large: '1.25rem' }}
          >
            {arrangements.map((arrangement, index) => (
              <TourArrangementCard item={arrangement.elements} variant="arrangement" key={index} />
            ))}
          </Grid>
        </Container>
        <TripAdvisor />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default ToursPage

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const content = await getPageByCodename<ToursPageModel>({
    codename: 'tours_page',
    lang: locale,
    preview,
  })
  return {
    props: {
      ...(await getServerSideTranslations(locale)),
      ...content,
    },
  }
}
